import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Grid, Link } from "theme-ui";

import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { BackArrowIcon } from "components/svg";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import TabSection from "containers/Clients/components/TabSection";

import { createLead, getLead, getLeadOptions, toDueDate, checkLeadIdentity, noteOnExistingClient, LEAD_STATUS_LABELS, type LeadInput, type LeadStatus, type IdentityMatch } from "api/Leads";

/**
 * New lead (s.14.4 - entry by form), in the client form's language: the
 * app's TextInput / SelectInput, section cards, Back link, H2. The minimum
 * that makes a useful row on the work queue: a name, who to talk to, who
 * owns it, and what to do next. Everything else is edited on the record.
 */

export const LEAD_SOURCES = [
  { value: "referral", label: "Referral" },
  { value: "web", label: "Web" },
  { value: "list_import", label: "List import" },
  { value: "proposal_generator", label: "Proposal generator" },
  { value: "trade_show", label: "Trade show" },
  { value: "cold_outreach", label: "Cold outreach" },
  { value: "manual", label: "Other" },
];

const NewLead = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // ?cloneFrom=<leadId>: a second person at the same organization
  // (LEADS_NEXT.md s.4). Organization fields are copied, contact fields
  // start blank and must name a different person.
  const cloneFrom = params.get("cloneFrom");
  const [cloneName, setCloneName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [owners, setOwners] = useState<Array<{ id: string; name: string }>>([]);
  // Shared identity check (docs/leads/LEADS_NEXT.md s.1, s.4): runs when
  // the organization, website, contact or email field is left, and again
  // on Create. A CLIENT match never becomes a lead - the enquiry is
  // offered as a note on the client instead. A LEAD match is either the
  // same person (created, but flagged as a possible duplicate - or
  // refused when cloning) or a colleague (a sibling lead; fine).
  const [match, setMatch] = useState<IdentityMatch | null>(null);
  const [noteText, setNoteText] = useState("");
  const [noting, setNoting] = useState(false);
  const [form, setForm] = useState<LeadInput>({
    name: "",
    contactName: "",
    contactTitle: "",
    contactEmail: "",
    phone: "",
    website: "",
    leadStatus: "new",
    leadSource: "referral",
    leadOwnerId: "",
    nextActionAt: "",
    nextActionNote: "",
    address: { line1: "", city: "", state: "", postalCode: "" },
  });

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | New lead";
    getLeadOptions().then((o) => setOwners(o.owners));
    if (cloneFrom) {
      getLead(cloneFrom).then((src: any) => {
        if (!src || src.error) return;
        setCloneName(src.name);
        setForm((f) => ({
          ...f,
          name: src.name ?? "",
          website: src.website ?? "",
          category: src.category ?? null,
          leadSource: src.leadSource ?? f.leadSource,
          leadOwnerId: src.leadOwner?.id ?? src.leadOwnerId ?? "",
          address: { line1: src.address?.line1 ?? "", city: src.address?.city ?? "", state: src.address?.state ?? "", postalCode: src.address?.postalCode ?? "" },
        }));
      });
    }
  }, [cloneFrom]);

  const set = (k: keyof LeadInput) => (v: any) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k: string) => (v: string) => setForm((f) => ({ ...f, address: { ...(f.address ?? {}), [k]: k === "state" ? String(v).toUpperCase().slice(0, 2) : v } }));

  const check = async () => {
    if (!form.name.trim() && !form.contactEmail && !form.website) return setMatch(null);
    const hit = await checkLeadIdentity({ name: form.name.trim() || null, contactEmail: form.contactEmail || null, website: form.website || null, contactName: form.contactName || null });
    setMatch(hit);
    if (hit?.kind === "client" && !noteText) setNoteText(enquiryNote());
  };

  /** What the operator typed, as the note that goes on the client instead of a new lead. */
  const enquiryNote = () => {
    const who = [form.contactName, form.contactTitle, form.contactEmail, form.phone].filter(Boolean).join(" \u00b7 ");
    const lines = [
      `Enquiry entered manually${form.leadSource ? ` (source: ${LEAD_SOURCES.find((s) => s.value === form.leadSource)?.label ?? form.leadSource})` : ""}`,
      who ? `From: ${who}` : "",
      form.nextActionNote ? `Next step: ${form.nextActionNote}${form.nextActionAt ? ` (by ${form.nextActionAt})` : ""}` : "",
    ].filter(Boolean);
    return lines.join("\n");
  };

  const addNote = async () => {
    if (!match || match.kind !== "client") return;
    setNoting(true);
    setError(null);
    const res: any = await noteOnExistingClient(match.id, noteText);
    setNoting(false);
    if (res?.error) return setError(res.error);
    navigate(`/clients/${match.id}`);
  };

  const submit = async () => {
    if (!form.name.trim()) return setError("A lead needs a name.");
    if (cloneFrom && !(form.contactName ?? "").trim() && !(form.contactEmail ?? "").trim()) return setError("A cloned lead needs its own contact name or email.");
    setBusy(true);
    setError(null);
    const addr = form.address ?? {};
    const res: any = await createLead({
      ...form,
      cloneOf: cloneFrom || null,
      name: form.name.trim(),
      contactName: form.contactName || null,
      contactTitle: form.contactTitle || null,
      contactEmail: form.contactEmail || null,
      phone: form.phone || null,
      website: form.website || null,
      leadOwnerId: form.leadOwnerId || null,
      nextActionAt: toDueDate(form.nextActionAt),
      nextActionNote: form.nextActionNote || null,
      address: Object.values(addr).some(Boolean) ? addr : null,
    });
    setBusy(false);
    if (res?.error) {
      if (res.existing) {
        setMatch(res.existing);
        if (res.existing.kind === "client" && !noteText) setNoteText(enquiryNote());
      }
      return setError(res.error);
    }
    navigate(`/leads/${res.id}`);
  };

  const isClientMatch = match?.kind === "client";
  const isSamePersonLead = match?.kind === "lead" && match.samePerson;
  const isSiblingLead = match?.kind === "lead" && !match.samePerson;

  return (
    <>
      <Header />
      <Page backAction={<Link variant="back" href="/leads"><BackArrowIcon />Back to Leads</Link>}>
        <H2>{cloneFrom ? "New contact at " + (cloneName ?? "this organization") : "New lead"}</H2>
        <P fontFamily="Poppins" fontSize={"13px"} color={themevals.colors.dkgrey} marginTop={"4px"} marginBottom={"20px"}>
          {cloneFrom
            ? "A second person at the same organization gets a lead of their own. Fill in who they are; the organization details are copied."
            : "A name and a next step are enough to put it on the queue. Everything else can be filled in on the record."}
        </P>

        {error && (
          <Box sx={{ marginBottom: "16px", padding: "10px 14px", background: "#FBEAEA", color: "#A60000", borderRadius: "8px", fontFamily: "Poppins", fontSize: "13px" }}>{error}</Box>
        )}

        {match && !(isSiblingLead && cloneFrom) && (
          <Box
            sx={{
              marginBottom: "16px",
              padding: "14px 16px",
              background: isClientMatch ? "#EEF4FB" : isSamePersonLead ? "#FFF6E5" : "#F1EFE8",
              border: `1px solid ${isClientMatch ? "#B9CFEA" : isSamePersonLead ? "#F2D28B" : "#D9D6CC"}`,
              borderRadius: "8px",
              fontFamily: "Poppins",
              fontSize: "13px",
              color: themevals.colors.black,
            }}
          >
            <Box sx={{ fontWeight: 600 }}>
              {isClientMatch ? "This is already a client: " : isSamePersonLead ? "This looks like the same person as lead " : "Same organization as lead "}
              <Link href={isClientMatch ? `/clients/${match.id}` : `/leads/${match.id}`} sx={{ color: "#1a2332" }}>{match.name}</Link>
            </Box>
            <Box sx={{ color: themevals.colors.dkgrey, marginTop: "2px" }}>{match.reason}.</Box>
            {isClientMatch ? (
              <Box sx={{ marginTop: "12px" }}>
                <Box sx={{ marginBottom: "8px" }}>A lead is never created for an existing client. Add this enquiry as a note on the client instead:</Box>
                <TextInput name="enquiryNote" type="textarea" rows={4} value={noteText} onChange={(v: any) => setNoteText(v)} />
                <Box sx={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                  <BasicButton type="button" onClick={addNote} disabled={noting || !noteText.trim()} $submitting={noting}>Add as a note to {match.name}</BasicButton>
                  <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate(`/clients/${match.id}`)}>Open the client</BasicButton>
                </Box>
              </Box>
            ) : isSamePersonLead ? (
              <Box sx={{ marginTop: "8px" }}>
                {cloneFrom
                  ? "A clone must name a different person. Change the contact name or email."
                  : "You can still create this lead; it will be flagged as a possible duplicate and can be merged from Leads \u203a Duplicates."}
              </Box>
            ) : (
              <Box sx={{ marginTop: "8px" }}>A different person at the same prospect is a lead of its own. When either converts they end up under one client.</Box>
            )}
          </Box>
        )}

        <Box sx={{ maxWidth: "920px" }}>
          <TabSection title="Organization" bodyPadding>
            <Grid gap={"16px"} columns={[1, "2fr 1fr"]}>
              <TextInput name="name" label="Organization" required value={form.name} onChange={set("name")} onBlur={check} />
              <TextInput name="website" label="Website" placeholder="https://" value={form.website ?? ""} onChange={set("website")} onBlur={check} />
            </Grid>
            <Grid gap={"16px"} columns={[1, "2fr 1fr 90px 120px"]} sx={{ marginTop: "16px" }}>
              <TextInput name="line1" label="Address" placeholder="Street" value={form.address?.line1 ?? ""} onChange={setAddr("line1")} />
              <TextInput name="city" label="City" value={form.address?.city ?? ""} onChange={setAddr("city")} />
              <TextInput name="state" label="State" value={form.address?.state ?? ""} onChange={setAddr("state")} />
              <TextInput name="postalCode" label="ZIP" value={form.address?.postalCode ?? ""} onChange={setAddr("postalCode")} />
            </Grid>
          </TabSection>

          <Box sx={{ marginTop: "24px" }}>
            <TabSection title="Contact" subtitle="the person you are talking to" bodyPadding>
              <Grid gap={"16px"} columns={[1, 2, 4]}>
                <TextInput name="contactName" label="Name" value={form.contactName ?? ""} onChange={set("contactName")} onBlur={check} />
                <TextInput name="contactTitle" label="Title" value={form.contactTitle ?? ""} onChange={set("contactTitle")} />
                <TextInput name="contactEmail" label="Email" type="email" value={form.contactEmail ?? ""} onChange={set("contactEmail")} onBlur={check} />
                <TextInput name="phone" label="Phone" value={form.phone ?? ""} onChange={set("phone")} />
              </Grid>
            </TabSection>
          </Box>

          <Box sx={{ marginTop: "24px" }}>
            <TabSection title="Working it" subtitle="status, source, owner and the next step" bodyPadding>
              <Grid gap={"16px"} columns={[1, 3]}>
                <SelectInput
                  name="leadStatus"
                  label="Status"
                  value={form.leadStatus}
                  options={(["new", "contacted", "qualified"] as LeadStatus[]).map((s) => ({ value: s, label: LEAD_STATUS_LABELS[s] }))}
                  onChange={set("leadStatus")}
                />
                <SelectInput name="leadSource" label="Source" value={form.leadSource ?? ""} options={LEAD_SOURCES} onChange={set("leadSource")} />
                <SelectInput
                  name="leadOwnerId"
                  label="Owner"
                  value={form.leadOwnerId ?? ""}
                  clearable
                  options={[{ value: "", label: "Me" }, ...owners.map((o) => ({ value: o.id, label: o.name }))]}
                  onChange={set("leadOwnerId")}
                />
              </Grid>
              <Grid gap={"16px"} columns={[1, "200px 1fr"]} sx={{ marginTop: "16px" }}>
                <TextInput name="nextActionAt" label="Next action due" type="date" value={form.nextActionAt ?? ""} onChange={set("nextActionAt")} />
                <TextInput name="nextActionNote" label="Next action" placeholder="e.g. Intro call, then RFQ" value={form.nextActionNote ?? ""} onChange={set("nextActionNote")} />
              </Grid>
            </TabSection>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "24px" }}>
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate("/leads")}>Cancel</BasicButton>
            <BasicButton type="button" onClick={submit} disabled={busy || isClientMatch || (!!cloneFrom && isSamePersonLead) || !form.name.trim()} $submitting={busy}>{cloneFrom ? "Create lead for this contact" : "Create lead"}</BasicButton>
          </Box>
        </Box>
      </Page>
    </>
  );
};

export default NewLead;
